import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
import { ChartBlockView } from "../ui/ChartBlockView";

export const CHART_BLOCK_NAME = "chartBlock";

// Free-text attrs (the source passage, the edit prompt) can contain newlines and
// quotes. Stored raw, a multi-line value splits the `<chart-block>` tag during
// Markdown serialization (a blank line ends the HTML block), so the tag loses its
// closing `>` and renders as plain text. URL-encoding keeps the attribute on one line.
const encodeAttr = (value: string) => encodeURIComponent(value);
const decodeAttr = (raw: string) => {
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw; // legacy (un-encoded) value or stray "%" — use as-is
  }
};

export const ChartBlock = Node.create({
  name: CHART_BLOCK_NAME,
  group: "block",
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      chartId: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-chart-id") ?? "",
        renderHTML: (attrs) => ({ "data-chart-id": attrs.chartId })
      },
      title: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-title") ?? "",
        renderHTML: (attrs) => ({ "data-title": attrs.title })
      },
      imageUrl: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-image-url") ?? "",
        renderHTML: (attrs) => ({ "data-image-url": attrs.imageUrl })
      },
      sourceText: {
        default: "",
        parseHTML: (el) => decodeAttr(el.getAttribute("data-source-text") ?? ""),
        renderHTML: (attrs) => (attrs.sourceText ? { "data-source-text": encodeAttr(attrs.sourceText) } : {})
      },
      prompt: {
        default: "",
        parseHTML: (el) => decodeAttr(el.getAttribute("data-prompt") ?? ""),
        renderHTML: (attrs) => (attrs.prompt ? { "data-prompt": encodeAttr(attrs.prompt) } : {})
      },
      status: {
        default: "default",
        parseHTML: (el) => el.getAttribute("data-status") ?? "default",
        renderHTML: (attrs) => (attrs.status === "default" ? {} : { "data-status": attrs.status })
      }
    };
  },

  parseHTML() {
    return [{ tag: "chart-block" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["chart-block", mergeAttributes(HTMLAttributes)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ChartBlockView);
  }
});
